import {yupResolver} from '@hookform/resolvers/yup'
import {HubConnection, HubConnectionBuilder, JsonHubProtocol, LogLevel} from '@microsoft/signalr'
import {
    Box,
    Button, Dialog, DialogActions, DialogContent, Divider,
    Grid,
    MobileStepper, Stack, Tooltip,
    Typography
} from '@mui/material'
import React, {useEffect, useState} from 'react'
import {FieldValues, useForm} from 'react-hook-form'
import {toast} from 'react-toastify'
import {useRecoilValue, useSetRecoilState} from 'recoil'
import {
    CoreImportService,
    PayrollImportData
} from '../../../../Component/Api/Import/CoreImportService'
import AppMultiSelect from '../../../../Component/Common/Components/FormValidationInputs/AppMultiSelect'
import AppSelectList, {
    IDropDownOptions
} from '../../../../Component/Common/Components/FormValidationInputs/AppSelectList'
import {GetAllScorecardsResponse} from '../../../../Component/Model/Dto/ScorecardLicense'
import {ExportKeyValue} from '../../../../Component/Model/Dto/Upload'
import {ImportSource, RequestStatus} from '../../../../Component/Model/Enum/ImportEnum'
import {getIOptionsArray} from '../../../../Helpers/arrayHelper'
import {defaultObject} from '../../../../Helpers/typescriptHelper'
import {
    VerificationWorkflowService
} from '../../../../Component/Api/VerificationAgency/VerificationWorkflow/VerificationWorkflowService'
import {EE123PeopleRole_Edit, PeopleEditRole} from '../../../../Component/Common/Constants/UserRoles'
import {userRolesState} from '../../../../State/user.Atom'
import {OrganisationAtom} from "../../../../State/Organisation.Atom";
import {OrganisationService} from "../../../../Component/Api/Organisation/OrganisationService";
import {RequestTrackerService} from "../../../../Component/Api/Staging/RequestTrackerService";
import {RequestTracker} from "../../../../Component/Model/Dto/Staging/RequestTracker";
import {
    InfoOutlined,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    LockOutlined
} from "@mui/icons-material";
import {Link, useHistory} from "react-router-dom";
import {RequestTrackerContent} from "./RequestTrackerContent";
import {
    ImportManagement
} from "../../../../Component/Model/Dto/VerificationAgency/Client/ImportManagement/ImportManagement";
import {ImportProgressBarSummarize} from "../../../../Component/Common/Components/ProgressBar/ImportProgressSummarize";
import {PayrollImportValidationSchema} from "./PayrollImportValidationSchema";
import AppAccordion from "../../../../Component/Common/Components/Accordion/AppAccordion";
import {CustomDialogTitle} from "../../../Common/Custom/CustomDialogTitle";
import {MDBox} from "../../../../Component/Common/Components/MDBox";
import {MDTypography} from "../../../../Component/Common/Components/MDTypography";
import {MDButton} from "../../../../Component/Common/Components/MDButton";
import {LoadingButton} from "@mui/lab";
import {SkeletonLoading} from "../../../../Component/Common/SkeletonLoading/SkeletonLoading";
import {useSignalRConnection} from "../../../../Hooks/useSignalRConnection";

export const PayrollImport = () => {
    const setOrganisationAtom = useSetRecoilState(OrganisationAtom);
    const organisation = useRecoilValue(OrganisationAtom);
    const [scorecardData, setScorecardData] = useState<GetAllScorecardsResponse>(defaultObject);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [progressData, setProgressData] = useState<{
        [scorecardId: string]: { [importTypeId: number]: ImportManagement.ImportDetailSheetDto }
    }>({});
    const [isImporting, setIsImporting] = useState<boolean>(false);
    const [requestTracker, setRequestTracker] = useState<RequestTracker.RequestTrackerDetail>();
    const [organisations, setOrganisations] = useState<IDropDownOptions[]>([]);
    const [importDetails, setImportDetails] = useState<ImportManagement.ImportDetailDto[]>([]);
    const [activeStep, setActiveStep] = useState(0);
    const history = useHistory();
    const iconWidth = 45;
    const iconHeight = 45;
    const maxTiles = 8;
    const [useSignalR, setUseSignalR] = useState<boolean>(false);
    const [isImportInProgressForOrganistation, setIsImportInProgressForOrganistation] = useState<boolean>(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const hub = useSignalRConnection("ImportHub");

    const {control, handleSubmit, watch, reset, setValue, trigger} = useForm({
        mode: 'all',
        resolver: yupResolver(PayrollImportValidationSchema)
    });

    const selectedOrganisation = watch('organisationId');
    const userRoles = useRecoilValue(userRolesState);
    const hasBEEImportRole = userRoles.includes(PeopleEditRole);
    const hasEEImportRole = userRoles.includes(EE123PeopleRole_Edit);

    const findScorecardName = (scorecardId: string): string => {
        const allScorecards = [
            ...(scorecardData.beeScorecards ?? []),
            ...(scorecardData.eeScorecards ?? [])
        ];
        return allScorecards.find(x => x.scorecardId === scorecardId)?.scorecardName ?? "";
    };

    useEffect(() => {
        getOrganisations();
        if (organisation.OrganisationId > 0) {
            const updatedOrganisationData = {
                ...defaultObject,
                organisationId: organisation.OrganisationId
            };
            reset(updatedOrganisationData);
        }
    }, []);

    const loadOrganisationData = async (organisationId: number) => {
        setIsLoading(true);
        try {
            await getOrganisationRelatedData(organisationId);
            await getOrganisationImportsInProgress(organisationId);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (selectedOrganisation !== undefined && selectedOrganisation > 0) {
            loadOrganisationData(selectedOrganisation);
            setValue("beeScorecards", []);
            setValue("eeScorecards", []);
        } else {
            setRequestTracker(undefined);
        }
    }, [selectedOrganisation]);

    useEffect(() => {
        if (selectedOrganisation && organisations.length > 0) {
            const OrganisationName = organisations.find(f => f.id == selectedOrganisation)?.value ?? "";
            setOrganisationAtom({
                OrganisationId: selectedOrganisation,
                OrganisationName,
                ShowPayrollIntegrationMenuItems: true
            });
        }
    }, [selectedOrganisation, organisations]);

    const getOrganisations = async () => {
        try {
            setIsLoading(true);
            const organisationResponse = await OrganisationService.getOrganisationsWithPayroll();

            const organisationData: IDropDownOptions[] = organisationResponse.data
                .filter(item => item.organisationId !== undefined)
                .map(item => ({
                    id: item.organisationId!,
                    value: item.registrationName,
                }));

            setOrganisations(organisationData);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const getOrganisationRelatedData = async (organisationId: number) => {
        try {
            const scorecardResponse = await OrganisationService.getOrganisationScorecards(organisationId);
            const requestTrackerResponse = await RequestTrackerService.getLatestRequestTracker(organisationId)

            setScorecardData(scorecardResponse.data);
            if (requestTrackerResponse.data) {
                setRequestTracker(requestTrackerResponse.data);
            } else {
                setRequestTracker(undefined);
            }
        } catch (error) {
            console.log(error);
        }
    }

    const getOrganisationImportsInProgress = async (organisationId: number) => {
        try {
            const organisationImportDetails = await VerificationWorkflowService.GetOrganisationImportDetails(organisationId, ImportSource.Payroll);

            if (organisationImportDetails.data?.length > 0) {
                const initialProgressData: {
                    [scorecardId: string]: { [importTypeId: number]: ImportManagement.ImportDetailSheetDto }
                } = {};

                organisationImportDetails.data.forEach(importDetail => {
                    const scorecardId = importDetail.scorecardId;
                    initialProgressData[scorecardId] = {};

                    importDetail.progressSheets?.forEach(sheet => {
                        initialProgressData[scorecardId][sheet.importTypeId] = {...sheet};
                    });
                });

                setProgressData(initialProgressData);
                setImportDetails(organisationImportDetails.data);
                setUseSignalR(true);
                setIsImportInProgressForOrganistation(true);
            } else {
                setIsImportInProgressForOrganistation(false);
            }
        } catch (error) {
            console.log(error);
        }
    }

    const handleDataSubmit = async (data: FieldValues) => {
        setProgressData(defaultObject);
        setIsImporting(true);
        try {
            const parameters: ExportKeyValue[] = [];
            data.eeScorecards = data.eeScorecards || [];
            data.beeScorecards = data.beeScorecards || [];
            const scorecardIds = [...data.beeScorecards, ...data.eeScorecards];

            const inProgressScorecards: string[] = [];
            for (const scorecardId of scorecardIds) {

                const isInProgress = await checkForInProgressImport(scorecardId);
                if (isInProgress) {
                    inProgressScorecards.push(scorecardId);

                }
            }
            if (inProgressScorecards.length > 0) {
                for (const id of inProgressScorecards) {
                    toast.error(`An import is already in progress for scorecard: ${findScorecardName(id)}`);
                }
                setIsImporting(false);
                return;
            }

            const request: PayrollImportData = {
                beeScorecardIds: data.beeScorecards,
                eeScorecardIds: data.eeScorecards,
                parameters: parameters,
                organisationId: selectedOrganisation,
                requestId: requestTracker?.requestId ?? 0
            };
            const result = await CoreImportService.ImportPayrollData(request);
            if (result.data) {
                getOrganisationImportsInProgress(selectedOrganisation);
            }

        } catch (error) {
            console.log(error);
            toast.error('Import Failed');
        } finally {
            setIsImporting(false);
        }
    }

    const updateProgress = (
        scorecardId: string,
        importTypeId: number,
        updates: Partial<ImportManagement.ImportDetailSheetDto>
    ) => {
        setProgressData(prev => ({
            ...prev,
            [scorecardId]: {
                ...(prev[scorecardId] ?? {}),
                [importTypeId]: {
                    ...(prev[scorecardId]?.[importTypeId] ?? {}),
                    ...updates,
                },
            },
        }));
    };

    useEffect(() => {
        if (!useSignalR) return;

        const handlers: { eventName: string; callback: (...args: any[]) => void }[] = [];

        const registerHandler = (eventName: string, callback: (...args: any[]) => void) => {
            hub.on(eventName, callback);
            handlers.push({ eventName, callback });
        };

        (async () => {
            try {
                if (!hub.isConnected) {
                    await hub.start();
                }

                importDetails.forEach(importDetail => {
                    const scorecardId = importDetail.scorecardId;
                    const importManagementId = importDetail.id;
                    const identifier = `payroll-${scorecardId}-${selectedOrganisation}-${importManagementId}`;

                    registerHandler(
                        `ImportInProgress-${identifier}`,
                        data => {
                            const { CurrentScorecard, ImportTypeId, progress, LastSuccessfulStep } = data;
                            updateProgress(CurrentScorecard, ImportTypeId, {
                                progressPercentage: progress,
                                lastSuccessfulStep: LastSuccessfulStep,
                            });
                        }
                    );

                    registerHandler(
                        `ImportSuccess-${identifier}`,
                        data => {
                            const { CurrentScorecard, ImportTypeId, NumberOfRowsCorrect, NumberOfRowsFailed, Log } = data;
                            updateProgress(CurrentScorecard, ImportTypeId, {
                                progressPercentage: 100,
                                importSuccessful: true,
                                numberOfRowsCorrect: NumberOfRowsCorrect,
                                numberOfRowsFailed: NumberOfRowsFailed,
                                log: Log,
                            });
                        }
                    );

                    registerHandler(
                        `ImportFailed-${identifier}`,
                        data => {
                            const {
                                CurrentScorecard,
                                ImportTypeId,
                                NumberOfRowsCorrect,
                                NumberOfRowsFailed,
                                Log,
                                ErrorBlobUrl,
                                LastSuccessfulStep,
                                isCompleted
                            } = data;

                            updateProgress(CurrentScorecard, ImportTypeId, {
                                progressPercentage: 0,
                                importSuccessful: false,
                                numberOfRowsCorrect: NumberOfRowsCorrect,
                                numberOfRowsFailed: NumberOfRowsFailed,
                                log: Log,
                                errorBlobUrl: ErrorBlobUrl,
                                lastSuccessfulStep: LastSuccessfulStep,
                            });

                            if (isCompleted !== undefined && !isCompleted) {
                                toast.error(`Import Failed for ${importDetail.scorecardName}. Please try again`);
                            }
                        }
                    );
                });

                registerHandler(
                    `ImportFailed-payroll-${selectedOrganisation}`,
                    () => {
                        toast.error(`Import Failed for selected scorecards. Please try again`);
                        setIsImportInProgressForOrganistation(false);
                    }
                );

                registerHandler(
                    `ImportComplete-payroll-${selectedOrganisation}`,
                    () => {
                        setIsImportInProgressForOrganistation(false);
                    }
                );

            } catch (error) {
                console.error("SignalR connection failed:", error);
            }
        })();

        return () => {
            handlers.forEach(h => hub.off(h.eventName, h.callback));
        };
    }, [useSignalR, importDetails]);


    const requestEmployees = async () => {
        setIsLoading(true);
        try {
            const response = await RequestTrackerService.requestEmployees(selectedOrganisation);
            if (response.data) {
                toast.success("Successfully requested employees.");
                getOrganisationRelatedData(selectedOrganisation);
            } else {
                toast.error(`A request to fetch employees is already in progress.`);
            }

        } catch (error) {
            console.log(error)
            toast.error("An error has occurred");
        } finally {
            setIsLoading(false);
        }
    }

    const checkForInProgressImport = async (scorecardId: string): Promise<boolean> => {
        try {
            const result = await VerificationWorkflowService.IsImportInProgressForScorecard(scorecardId);
            return !!result.data;
        } catch (error) {
            console.log(error);
            toast.error("An error has occurred");
            return false;
        }
    };

    const openPayrollIImport = () => {
        const OrganisationName = organisations.find(f => f.id == selectedOrganisation)?.value ?? "";
        setOrganisationAtom({
            OrganisationId: selectedOrganisation ?? -1,
            OrganisationName,
            ShowPayrollIntegrationMenuItems: true
        });
        history.push(`/organisation/payrollData/fetchData`);
    }

    const hasScorecards = (): boolean => {
        return scorecardData.eeScorecards?.length > 0 || scorecardData.beeScorecards?.length > 0;
    }

    const disableImportButton = (): boolean => {
        return !(hasScorecards() && requestTracker?.status === RequestStatus.ImportReady);
    }

    const handleOpenConfirmation = async () => {
        // validates the form
        const isValid = await trigger();
        if (isValid) {
            setShowConfirmationDialog(true); //
        }
    };

    const renderSheetsForStep = (scorecardId: string, stepIndex: number) => {
        const sheets = progressData[scorecardId];
        if (!sheets) return null;

        const importTypeIds = Object.keys(sheets);
        const startIndex = stepIndex * maxTiles;
        const endIndex = Math.min(startIndex + maxTiles, importTypeIds.length);

        return importTypeIds.slice(startIndex, endIndex).map(id => {
            const sheet = sheets[Number(id)];
            return (
                <ImportProgressBarSummarize
                    value={sheet.progressPercentage ?? 0}
                    iconHeight={iconHeight}
                    iconWidth={iconWidth}
                    progressData={{
                        importTypeId: sheet.importTypeId,
                        importName: sheet.dataPageName,
                        numberOfRowsCorrect: sheet.numberOfRowsCorrect,
                        numberOfRowsFailed: sheet.numberOfRowsFailed,
                        isSuccess: sheet.importSuccessful,
                    }}
                />
            );
        });
    };

    if (isLoading) {
        return <SkeletonLoading formLoading FieldAmount={6} width={"100%"}/>
    }
        return (
            <>
                {organisations.length == 0 &&
                    <Box style={{
                        position: 'absolute',
                        left: 0,
                        width: '100%',
                        height: '80%',
                        backgroundColor: '#05050585',
                        zIndex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column'
                    }}>
                        <Box mb={2}>
                            <LockOutlined style={{color: 'white', width: 150, height: 150}}/>
                        </Box>
                        <Typography variant='h3' fontWeight='bold' style={{color: 'white'}}>
                            There are no payrolls connected to your organisation(s)
                        </Typography>
                        <Typography variant='h3' fontWeight='bold' style={{color: 'white'}}>
                            Please connect one or contact your administrator.
                        </Typography>
                    </Box>
                }

                <Typography variant='h4' fontWeight='bold' style={{paddingBottom: "15px"}}>
                    Central Data Management - Payroll Imports
                </Typography>

                <Grid container spacing={2} component="form" id='PayrollImportForm'
                      onSubmit={handleSubmit(handleDataSubmit)}>
                    <Grid item xs={12}>
                        <Typography variant='body2' color="secondary">
                            Here you can import data fetched from your payroll for an organisation into the scorecard(s)
                            you select.
                        </Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Stack spacing={2} alignItems="flex-start">
                            <AppSelectList control={control} name="organisationId" label="Organisation"
                                           items={organisations}/>

                            {scorecardData.beeScorecards && scorecardData.beeScorecards.length > 0 && (
                                <>
                                    <AppMultiSelect
                                        disabled={isImporting || isImportInProgressForOrganistation || !hasBEEImportRole}
                                        control={control}
                                        hintText='Search or select an option'
                                        name="beeScorecards"
                                        label="BEE Scorecards"
                                        options={getIOptionsArray(scorecardData.beeScorecards, "scorecardId", "scorecardName")}
                                        size="small"
                                    />
                                    {!hasBEEImportRole &&
                                        <Typography style={{fontSize: '15px', color: 'red'}}>
                                            You don't have permission to edit BEE People
                                        </Typography>}
                                </>
                            )}

                            {scorecardData.eeScorecards && scorecardData.eeScorecards.length > 0 && (
                                <>
                                    <AppMultiSelect
                                        disabled={isImporting || isImportInProgressForOrganistation || !hasEEImportRole}
                                        control={control}
                                        hintText='Search or select an option'
                                        name="eeScorecards"
                                        label="EE Scorecards"
                                        options={getIOptionsArray(scorecardData.eeScorecards, "scorecardId", "scorecardName")}
                                        size="small"
                                    />
                                    {!hasEEImportRole &&
                                        <Typography style={{fontSize: '15px', color: 'red'}}>
                                            You don't have permission to edit EE People
                                        </Typography>}
                                </>

                            )}
                            {selectedOrganisation > 0 && !hasScorecards() && (

                                <Typography
                                    style={{fontSize: '13px', color: 'red', paddingTop: '30px', paddingBottom: '20px'}}>
                                    You don’t have access to any scorecards linked to this organisation.
                                    Please contact your administrator to request access or have one assigned
                                </Typography>

                            )}

                            <LoadingButton
                                disabled={disableImportButton() || isImportInProgressForOrganistation}
                                onClick={handleOpenConfirmation}
                                loading={isImporting}
                                color="primary"
                                variant='contained'>
                                Begin Import
                            </LoadingButton>
                        </Stack>

                        <Dialog open={showConfirmationDialog} onClose={() => setShowConfirmationDialog(false)}
                                aria-labelledby="form-dialog-title" maxWidth='sm'>
                            <CustomDialogTitle id="customized-dialog-title"
                                               onClose={() => setShowConfirmationDialog(false)}>
                                Import Employees and Salaries
                            </CustomDialogTitle>
                            <DialogContent>
                                <MDBox>
                                    <MDTypography variant="button" fontWeight='regular' fontSize="0.9em"
                                                  sx={{whiteSpace: 'pre-line'}}>
                                        Note that importing will update both the Employee Structure and Employee
                                        Salaries
                                        with the latest information fetched from your payroll
                                    </MDTypography>
                                </MDBox>
                            </DialogContent>
                            <DialogActions>
                                <MDButton
                                    color="primary"
                                    variant="contained"
                                    onClick={() => {
                                        handleSubmit(handleDataSubmit)();
                                        setShowConfirmationDialog(false);
                                    }}>
                                    Import
                                </MDButton>
                            </DialogActions>
                        </Dialog>
                    </Grid>
                    <Grid item xs={6}>
                        <RequestTrackerContent
                            requestTracker={requestTracker}
                            requestEmployees={requestEmployees}
                            openPayrollIImport={openPayrollIImport}
                            selectedOrganisation={selectedOrganisation}/>
                    </Grid>
                </Grid>

                <Divider/>

                {importDetails.length > 0 &&
                    <Grid item xs={12}>
                        <Typography variant='body1' color="secondary">
                            Importing payroll data per scorecard
                        </Typography>
                        <Typography variant='body2' color="secondary" mb={1}>
                            We'll import all the data fetched from your payroll into the scorecards you selected.
                            <Tooltip title={<span>To view more information for imports in progress, click <Link
                                to={`payrollDetails/${useSignalR}`}>here</Link></span>}>
                                <InfoOutlined color='info' sx={{ml: 0.5}}/>
                            </Tooltip>
                        </Typography>
                    </Grid>
                }

                {
                    Object.entries(progressData).map(([scorecardId, sheets]) => {
                        const importDetail = importDetails.find(d => d.scorecardId === scorecardId);
                        const labelName = `${importDetail?.organisationName} | ${importDetail?.isEEScorecard ? 'EE' : 'BEE'} | ${importDetail?.scorecardName}`;
                        const importTypeIds = Object.keys(sheets);
                        const sheetMaxSteps = Math.ceil(importTypeIds.length / maxTiles);

                        return (

                            <AppAccordion key={scorecardId}
                                          expandOnDefault={true}
                                          label={
                                                <Box display="flex" alignItems="center">
                                                    <Tooltip title={<span>To view more information and/or errors, click <Link
                                                        to={`/organisation/payrollData/details/${importDetail?.scorecardId}/${importDetail?.id}/${useSignalR}`}>here</Link></span>}>
                                                        <InfoOutlined sx={{ mr: 1.5, color: "white"}}/>
                                                    </Tooltip>
                                                    <span>{labelName}</span>
                                                </Box>}
                            >
                                <Grid container item>
                                    <Grid item xs={12} sx={{backgroundColor: "white"}}>
                                        <Box
                                            sx={{
                                                height: "100%",
                                                width: "100%",
                                                pl: "15px",
                                                pt: "20px",
                                                pb: "20px",
                                            }}
                                        >
                                            <Grid container spacing={2}>
                                                {renderSheetsForStep(scorecardId, activeStep)}
                                            </Grid>
                                        </Box>
                                    </Grid>

                                    {sheetMaxSteps > 1 && (
                                        <Grid item xs={12}>
                                            <Box sx={{width: "100%"}}>
                                                <MobileStepper
                                                    variant="dots"
                                                    steps={sheetMaxSteps}
                                                    position="static"
                                                    activeStep={activeStep}
                                                    sx={{background: "white"}}
                                                    nextButton={
                                                        <Button
                                                            size="small"
                                                            onClick={() =>
                                                                setActiveStep(prev =>
                                                                    Math.min(prev + 1, sheetMaxSteps - 1)
                                                                )
                                                            }
                                                            disabled={activeStep === sheetMaxSteps - 1}
                                                        >
                                                            <KeyboardArrowRight/>
                                                        </Button>
                                                    }
                                                    backButton={
                                                        <Button
                                                            size="small"
                                                            onClick={() =>
                                                                setActiveStep(prev => Math.max(prev - 1, 0))
                                                            }
                                                            disabled={activeStep === 0}
                                                        >
                                                            <KeyboardArrowLeft/>
                                                        </Button>
                                                    }
                                                />
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>
                            </AppAccordion>

                        );
                    })
                }

            </>
        )
}